"use client";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { formatInr } from "@/utils/basicFunctions.util";
import { useId } from "react";
import { useMarginCalculator } from "./hooks";

interface RangeSetting {
  label: string;
  ariaLabel: string;
  min: number;
  max: number;
  step: number;
  initial: number;
}

export interface MarginCalculatorData {
  ariaLabel: string;
  billLabel: string;
  initialBill: number;
  billStep: number;
  rate: RangeSetting;
  margin: RangeSetting;
  cashbackLabel: string;
  marginLabel: string;
  shareLabel: string;
  // "of your margin on this bill, leaving {left}."
  leftTemplate: string;
  caption: string;
}

const panelClass =
  "grid content-start gap-3.5 rounded-3xl bg-white p-6 shadow-card";
const fieldClass = "grid gap-2 text-[15px] font-semibold";
const outputClass = "ml-1.5 font-extrabold text-primary";
const rowClass =
  "flex items-baseline justify-between border-b border-dashed border-line py-2 text-base";

// "What does a reward cost you?" - the preview's `margin_calculator`. A
// bill, a cashback rate and a gross margin; the panel shows the cashback as
// a share of the margin on that bill.
const MarginCalculator = ({
  ariaLabel,
  billLabel,
  initialBill,
  billStep,
  rate,
  margin,
  cashbackLabel,
  marginLabel,
  shareLabel,
  leftTemplate,
  caption,
}: MarginCalculatorData) => {
  const calc = useMarginCalculator({
    bill: initialBill,
    rate: rate.initial,
    margin: margin.initial,
  });
  const billId = useId();
  const [leftBefore, leftAfter] = leftTemplate.split("{left}");

  return (
    <figure role="group" aria-label={ariaLabel} className="m-0">
      <div className="grid items-start gap-[18px] lg:grid-cols-2">
        <div className={panelClass}>
          <label htmlFor={billId} className={fieldClass}>
            {billLabel}
          </label>
          <Input
            id={billId}
            type="number"
            inputMode="numeric"
            min={0}
            step={billStep}
            defaultValue={initialBill}
            onChange={(event) => calc.setBill(event.target.value)}
            className="h-auto rounded-[14px] border-[1.5px] border-line px-3.5 py-3 text-[22px] font-bold text-ink focus-visible:border-primary md:text-[22px]"
          />
          <div className={fieldClass}>
            <span>
              {rate.label}
              <output className={outputClass}>{calc.rate}%</output>
            </span>
            <Slider
              aria-label={rate.ariaLabel}
              min={rate.min}
              max={rate.max}
              step={rate.step}
              value={[calc.rate]}
              onValueChange={([value]) => calc.setRate(value)}
            />
          </div>
          <div className={fieldClass}>
            <span>
              {margin.label}
              <output className={outputClass}>{calc.margin}%</output>
            </span>
            <Slider
              aria-label={margin.ariaLabel}
              min={margin.min}
              max={margin.max}
              step={margin.step}
              value={[calc.margin]}
              onValueChange={([value]) => calc.setMargin(value)}
            />
          </div>
        </div>

        <div aria-live="polite" className={panelClass}>
          <div className={rowClass}>
            <span>{cashbackLabel}</span>
            <b className="text-[20px] font-bold tabular-nums">{formatInr(calc.cashback)}</b>
          </div>
          <div className={rowClass}>
            <span>{marginLabel}</span>
            <b className="text-[20px] font-bold tabular-nums">
              {formatInr(calc.grossMargin)}
            </b>
          </div>
          <div className="flex items-baseline justify-between rounded-[14px] bg-tone-blush px-3.5 py-3 text-base">
            <span>{shareLabel}</span>
            <b className="text-[28px] font-bold text-primary tabular-nums">
              {calc.share.toFixed(1)}%
            </b>
          </div>
          <span
            aria-hidden="true"
            className="block h-3 overflow-hidden rounded-full bg-paper"
          >
            <span
              className="block h-full bg-brand-gradient transition-[width] duration-300 motion-reduce:transition-none"
              style={{ width: `${Math.min(100, calc.share)}%` }}
            />
          </span>
          <p className="text-[15px] text-ink-muted">
            {leftBefore}
            <b className="text-ink">{formatInr(calc.left)}</b>
            {leftAfter}
          </p>
        </div>
      </div>
      <figcaption className="px-1 pt-3 text-[12.5px] text-ink-muted">{caption}</figcaption>
    </figure>
  );
};

export default MarginCalculator;
